/* eslint-disable react/prop-types */

import InputWithIcon from '@deliveroo/tools-component-library/dist/components/InputWithIcon';
import Button from '@deliveroo/tools-component-library/dist/components/Button';
import Text from '@deliveroo/tools-component-library/dist/components/Text';
import LayoutBox from '@deliveroo/tools-component-library/dist/components/LayoutBox';
import { bindActionCreators } from 'redux';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Link from 'next/link';
import get from 'lodash/get';
import Debug from 'debug';

import withI18n from 'lib/hocs/withI18n';
import { resetPassword } from '../../store/users/actions';
import styles from './styles.scss';

const debug = Debug('rs:components/PasswordReset');

class PasswordReset extends Component {
  static propTypes = {
    children: PropTypes.node,
    token: PropTypes.string.isRequired,
    i18n: withI18n.TYPE.isRequired,
  };

  static defaultProps = {
    children: undefined,
  };

  state = {
    remoteStatus: undefined,
    localError: undefined,
    password: '',
    confirmPassword: '',
  };

  onSubmit = (e) => {
    e.preventDefault();

    if (!this.localValidation(e)) return;

    const setStatus = (res) => this.setState({ remoteStatus: res });
    const { token } = this.props;
    const { password, confirmPassword } = this.state;

    this.props.actions
      .resetPassword({
        password1: password,
        password2: confirmPassword,
        token,
      })
      .then(setStatus)
      .catch(setStatus);
  };

  onPasswordChange = (event) => {
    const { value: password } = event.target;
    this.setState({
      password,
    });
  };

  onConfirmPasswordChange = (event) => {
    const { value: confirmPassword } = event.target;
    this.setState({
      confirmPassword,
    });
  };

  localValidation = () => {
    const { password, confirmPassword } = this.state;

    if (password !== confirmPassword) {
      this.setState({
        localError: 'MISMATCH',
      });
      return false;
    }
    if (password.length < 10) {
      this.setState({
        localError: 'TOO_SHORT',
      });
      return false;
    }

    this.setState({
      localError: undefined,
    });

    return true;
  };

  renderRemoteMessage(response = {}) {
    debug('render remote message', response.status);

    const { i18n } = this.props;
    const message = get(
      response,
      'payload.errors.password',
      i18n.gettext('password_reset.error.generic_error'),
    );

    switch (response.status) {
      case undefined:
        return undefined;
      case 404:
        return (
          <Text color="red">
            {i18n.gettext('password_reset.expired_link')} <br />
            <Link href="/forgotten-password">
              <a>{i18n.gettext('password_reset.expired_link.resend_link')}</a>
            </Link>
          </Text>
        );
      case 400:
        return <Text color="red">{message}</Text>;
      case 200:
        return (
          <Text>
            {i18n.gettext('password_reset.password_update_successful')}
          </Text>
        );
      default:
        return (
          <Text color="red">
            {i18n.gettext('password_reset.error.generic_error')}
          </Text>
        );
    }
  }

  renderLocalMessage(error) {
    debug('render local message');
    const { i18n } = this.props;

    switch (error) {
      case 'MISMATCH':
        return (
          <Text color="red">
            {i18n.gettext('password_reset.error.password_mismatch')}
          </Text>
        );
      case 'TOO_SHORT':
        return (
          <Text color="red">
            {i18n.gettext('password_reset.error.too_short')}
          </Text>
        );
      default:
        return (
          <Text color="red">
            {i18n.gettext('password_reset.error.generic_error')}
          </Text>
        );
    }
  }

  renderForm() {
    const { i18n } = this.props;
    const { remoteStatus, localError, password, confirmPassword } = this.state;

    const remoteMessage = this.renderRemoteMessage(remoteStatus);
    const localMessage = this.renderLocalMessage(localError);

    return (
      <div>
        <InputWithIcon
          spacing="0 0 small 0"
          placeholder={i18n.gettext(
            'passord_reset.form.password_input_placeholder',
          )}
          icon="lock_closed"
          type="password"
          value={password}
          onChange={this.onPasswordChange}
        />
        <InputWithIcon
          spacing="0 0 medium 0"
          placeholder={i18n.gettext(
            'passord_reset.form.password_confirm_input_placeholder',
          )}
          icon="lock_closed"
          type="password"
          value={confirmPassword}
          onChange={this.onConfirmPasswordChange}
        />

        {(remoteStatus || localError) && (
          <LayoutBox spacing="0 0 medium 0" data-testid="message">
            {remoteStatus && remoteMessage}
            {localError && localMessage}
          </LayoutBox>
        )}
        <Button
          label={i18n.gettext('password_reset.form.submit_label')}
          spacing="0 0 medium 0"
          className={styles.submitButton}
        />
      </div>
    );
  }

  renderSuccess() {
    const { i18n } = this.props;
    const { remoteStatus } = this.state;
    const remoteMessage = this.renderRemoteMessage(remoteStatus);

    return (
      <div>
        <LayoutBox spacing="0 0 medium 0">{remoteMessage}</LayoutBox>
        <Link href="/login">
          <a>{i18n.gettext('password_reset.success.back_to_login')}</a>
        </Link>
      </div>
    );
  }

  render() {
    debug('render');
    const { remoteStatus } = this.state;

    return (
      <div>
        <form className={styles.form} onSubmit={this.onSubmit}>
          {this.props.children && (
            <LayoutBox spacing="0 0 large 0">{this.props.children}</LayoutBox>
          )}
          {remoteStatus && remoteStatus.status === 200
            ? this.renderSuccess()
            : this.renderForm()}
        </form>
      </div>
    );
  }
}

export default connect(undefined, (dispatch) => ({
  actions: bindActionCreators({ resetPassword }, dispatch),
}))(withI18n(PasswordReset));

export { PasswordReset };
